/* eslint-disable @typescript-eslint/no-unnecessary-type-assertion */
import { type FC, useCallback, useEffect, useRef, useState } from 'react'

import fixWebmDuration from 'fix-webm-duration'
import { isMobile } from 'react-device-detect'
import { useTranslation } from 'react-i18next'
import { useResizeDetector } from 'react-resize-detector'
import { useStopwatch } from 'react-timer-hook'
import Webcam from 'react-webcam'

import { IconModalClose } from 'assets/svg/sprite'
import './_mediaRecorder.scss'
import { getSupportedMimeTypes } from 'utils/getSupportedMimeTypes'

interface Props {
    type: string
    status: string
    handleClose: () => void
    setRecordedChunks: (data: any) => void
    handleAudioStart: () => void
    handleAudioStop: () => void
}

const MAX_RECORDING_DURATION_MS = 5 * 60 * 1000
const MAX_RECORDING_SIZE_BYTES = 1 * 1024 * 1024 * 1024
const RECORDING_TIMESLICE_MS = 1000

const DEBUG_RECORDER = true

const formatDuration = (duration: {
    seconds: number
    minutes: number
    hours: number
}) => {
    const { seconds, minutes, hours } = duration
    return {
        seconds: seconds < 10 ? '0' + seconds : seconds.toString(),
        minutes: minutes < 10 ? '0' + minutes : minutes.toString(),
        hours: hours < 10 ? '0' + hours : hours.toString()
    }
}

const normalizeMimeType = (mimeType?: string | null): string => {
    if (!mimeType) return ''

    return mimeType.toLowerCase().split(';')[0].trim()
}

const formatBytes = (bytes: number): string => {
    if (!Number.isFinite(bytes)) return '0 B'

    const units = ['B', 'KB', 'MB', 'GB']
    let value = bytes
    let index = 0

    while (value >= 1024 && index < units.length - 1) {
        value = value / 1024
        index++
    }

    return `${value.toFixed(index === 0 ? 0 : 2)} ${units[index]}`
}

const logRecorder = (message: string, data?: any) => {
    if (!DEBUG_RECORDER) return

    if (data === undefined) {
        console.log(`[PostMediaRecorder] ${message}`)
    } else {
        console.log(`[PostMediaRecorder] ${message}`, data)
    }
}

const videoConstraints = {
    width: { ideal: 1920 },
    height: { ideal: 1080 },
    frameRate: { min: 15, ideal: 30, max: 30 },
    facingMode: 'user'
}

const PostMediaRecorder: FC<Props> = ({
    type,
    status,
    handleClose,
    setRecordedChunks,
    handleAudioStart,
    handleAudioStop
}) => {
    const [mediaRecordingSupported, setMediaRecordingSupported] = useState(true)

    const streamRef = useRef<MediaStream | null>(null)
    const webcamRef = useRef<Webcam>(null)
    const mediaRecorderRef = useRef<MediaRecorder>()
    const recordedChunksRef = useRef<Blob[]>([])
    const recordedSizeRef = useRef(0)
    const recordingTimeoutRef = useRef<number | null>(null)
    const isClosingRef = useRef(false)
    const recordingStartedAtRef = useRef<number | null>(null)

    const { t } = useTranslation()

    const { width, ref } = useResizeDetector({
        handleHeight: false,
        refreshMode: 'debounce',
        refreshRate: 50
    })

    const {
        seconds: sec,
        minutes: min,
        hours: h,
        isRunning,
        start,
        pause,
        reset
    } = useStopwatch({ autoStart: false })

    const { seconds, minutes } = formatDuration({
        seconds: sec,
        minutes: min,
        hours: h
    })

    const logStreamTracks = useCallback((label: string, stream?: MediaStream | null) => {
        if (!stream) {
            logRecorder(label, { stream: null })
            return
        }

        logRecorder(label, {
            streamId: stream.id,
            active: stream.active,
            tracks: stream.getTracks().map(track => ({
                kind: track.kind,
                id: track.id,
                label: track.label,
                enabled: track.enabled,
                muted: track.muted,
                readyState: track.readyState,
                settings:
                    typeof track.getSettings === 'function'
                        ? track.getSettings()
                        : null,
                constraints:
                    typeof track.getConstraints === 'function'
                        ? track.getConstraints()
                        : null
            }))
        })
    }, [])

    const clearRecordingTimeout = useCallback(() => {
        if (recordingTimeoutRef.current) {
            window.clearTimeout(recordingTimeoutRef.current)
            recordingTimeoutRef.current = null
            logRecorder('recording timeout cleared')
        }
    }, [])

    const stopCameraTracks = useCallback(() => {
        const webcamStream = webcamRef.current?.stream ?? null

        logStreamTracks('stopCameraTracks webcam stream before stop', webcamStream)
        logStreamTracks('stopCameraTracks streamRef before stop', streamRef.current)

        webcamStream?.getTracks().forEach(track => {
            logRecorder('stopping webcam track', {
                kind: track.kind,
                id: track.id,
                label: track.label,
                readyState: track.readyState
            })

            track.stop()
        })

        if (streamRef.current && streamRef.current.id !== webcamStream?.id) {
            streamRef.current.getTracks().forEach(track => {
                logRecorder('stopping streamRef track', {
                    kind: track.kind,
                    id: track.id,
                    label: track.label,
                    readyState: track.readyState
                })

                track.stop()
            })
        }

        logStreamTracks('stopCameraTracks webcam stream after stop', webcamStream)
        logStreamTracks('stopCameraTracks streamRef after stop', streamRef.current)

        streamRef.current = null
    }, [logStreamTracks])

    const calculateRecordingDurationMs = useCallback(() => {
        const now = Date.now()

        const durationFromStartRef = recordingStartedAtRef.current
            ? now - recordingStartedAtRef.current
            : 0

        const durationFromChunks =
            recordedChunksRef.current.length * RECORDING_TIMESLICE_MS

        const durationMs = Math.max(
            1000,
            durationFromStartRef,
            durationFromChunks
        )

        logRecorder('duration calculation', {
            recordingStartedAt: recordingStartedAtRef.current,
            now,
            durationFromStartRef,
            durationFromChunks,
            stopwatchState: {
                h,
                min,
                sec,
                stopwatchDurationMs: (h * 3600 + min * 60 + sec) * 1000
            },
            chunks: recordedChunksRef.current.length,
            durationMs
        })

        return durationMs
    }, [h, min, sec])

    const finalizeRecording = useCallback(
        async (fallbackMimeType?: string) => {
            const chunks = recordedChunksRef.current

            const rawMimeType =
                mediaRecorderRef.current?.mimeType ||
                chunks?.[0]?.type ||
                fallbackMimeType ||
                'video/webm'

            const normalizedMimeType = normalizeMimeType(rawMimeType)
            const durationMs = calculateRecordingDurationMs()

            let blob = new Blob(chunks, {
                type: normalizedMimeType || 'video/webm'
            })

            const originalBlob = blob

            logRecorder('finalizeRecording before duration fix', {
                rawMimeType,
                normalizedMimeType,
                durationMs,
                chunks: chunks.length,
                chunkSizes: chunks.map((chunk, index) => ({
                    index,
                    size: chunk.size,
                    sizeFormatted: formatBytes(chunk.size),
                    type: chunk.type
                })),
                originalBlobSize: originalBlob.size,
                originalBlobSizeFormatted: formatBytes(originalBlob.size),
                originalBlobType: originalBlob.type
            })

            if (normalizedMimeType === 'video/webm') {
                try {
                    blob = await fixWebmDuration(blob, durationMs, {
                        logger: false
                    })

                    logRecorder('fixWebmDuration success', {
                        durationMs,
                        beforeSize: originalBlob.size,
                        beforeSizeFormatted: formatBytes(originalBlob.size),
                        afterSize: blob.size,
                        afterSizeFormatted: formatBytes(blob.size),
                        beforeType: originalBlob.type,
                        afterType: blob.type
                    })
                } catch (error: any) {
                    console.error(
                        '[PostMediaRecorder] Failed to fix WebM duration',
                        error
                    )

                    logRecorder('fixWebmDuration failed; using original blob', {
                        message: error?.message,
                        name: error?.name
                    })

                    blob = originalBlob
                }
            }

            const extension =
                normalizedMimeType === 'video/mp4'
                    ? 'mp4'
                    : normalizedMimeType === 'video/quicktime'
                    ? 'mov'
                    : normalizedMimeType === 'video/x-m4v'
                    ? 'm4v'
                    : 'webm'

            const fixedFile = new File(
                [blob],
                `recording-${Date.now()}.${extension}`,
                {
                    type: blob.type || normalizedMimeType || 'video/webm',
                    lastModified: Date.now()
                }
            )

            logRecorder('finalized recording', {
                rawMimeType,
                normalizedMimeType,
                durationMs,
                durationSeconds: durationMs / 1000,
                chunks: chunks.length,
                originalSize: chunks.reduce((total, chunk) => total + chunk.size, 0),
                originalSizeFormatted: formatBytes(
                    chunks.reduce((total, chunk) => total + chunk.size, 0)
                ),
                fixedSize: fixedFile.size,
                fixedSizeFormatted: formatBytes(fixedFile.size),
                fixedType: fixedFile.type,
                fixedName: fixedFile.name
            })

            recordingStartedAtRef.current = null

            setRecordedChunks([fixedFile])
        },
        [calculateRecordingDurationMs, setRecordedChunks]
    )

    const closeRecorder = useCallback(() => {
        if (isClosingRef.current) {
            logRecorder('closeRecorder ignored; already closing')
            return
        }

        isClosingRef.current = true
        clearRecordingTimeout()

        const recorder = mediaRecorderRef.current

        logRecorder('closeRecorder called', {
            recorderExists: !!recorder,
            recorderState: recorder?.state,
            chunks: recordedChunksRef.current.length,
            recordedSize: recordedSizeRef.current,
            recordedSizeFormatted: formatBytes(recordedSizeRef.current)
        })

        if (recorder?.state === 'recording') {
            try {
                recorder.requestData()
                logRecorder('requestData called before recorder.stop')
            } catch (error: any) {
                logRecorder('requestData failed before recorder.stop', {
                    message: error?.message,
                    name: error?.name
                })
            }

            try {
                recorder.stop()
                logRecorder('recorder.stop called')
            } catch (error: any) {
                logRecorder('recorder.stop failed; finalizing manually', {
                    message: error?.message,
                    name: error?.name
                })

                finalizeRecording(recorder.mimeType)
            }
        } else {
            finalizeRecording(recorder?.mimeType)
        }

        reset()
        pause()
        stopCameraTracks()
        handleClose()
    }, [
        clearRecordingTimeout,
        finalizeRecording,
        handleClose,
        pause,
        reset,
        stopCameraTracks
    ])

    useEffect(() => {
        checkMediaPlayer(type)

        return () => {
            logRecorder('component unmount cleanup')

            clearRecordingTimeout()

            if (mediaRecorderRef.current?.state === 'recording') {
                try {
                    mediaRecorderRef.current.requestData()
                } catch {}

                try {
                    mediaRecorderRef.current.stop()
                } catch {}
            }

            stopCameraTracks()
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    const checkMediaPlayer = async (type: string) => {
        logRecorder('checkMediaPlayer requested', {
            type,
            constraints: {
                video: type === 'video',
                audio: true
            },
            videoConstraints
        })

        try {
            const stream = await navigator.mediaDevices.getUserMedia({
                video: type === 'video' ? videoConstraints : false,
                audio: true
            })

            streamRef.current = stream

            logRecorder('getUserMedia success')
            logStreamTracks('getUserMedia stream tracks', stream)
        } catch (err: any) {
            logRecorder('getUserMedia failed', {
                message: err?.message,
                name: err?.name,
                constraint: err?.constraint
            })

            setMediaRecordingSupported(false)
        }
    }

    const handleDataAvailable = useCallback(
        ({ data }: { data: Blob }) => {
            logRecorder('dataavailable', {
                size: data.size,
                sizeFormatted: formatBytes(data.size),
                type: data.type,
                recorderState: mediaRecorderRef.current?.state,
                previousChunks: recordedChunksRef.current.length,
                previousTotalSize: recordedSizeRef.current,
                previousTotalSizeFormatted: formatBytes(recordedSizeRef.current)
            })

            if (data.size <= 0) return

            recordedSizeRef.current += data.size

            if (recordedSizeRef.current > MAX_RECORDING_SIZE_BYTES) {
                logRecorder('max recording size exceeded', {
                    totalSize: recordedSizeRef.current,
                    totalSizeFormatted: formatBytes(recordedSizeRef.current),
                    maxSize: MAX_RECORDING_SIZE_BYTES,
                    maxSizeFormatted: formatBytes(MAX_RECORDING_SIZE_BYTES)
                })

                closeRecorder()
                return
            }

            recordedChunksRef.current.push(data)

            logRecorder('chunk stored', {
                chunks: recordedChunksRef.current.length,
                totalSize: recordedSizeRef.current,
                totalSizeFormatted: formatBytes(recordedSizeRef.current)
            })
        },
        [closeRecorder]
    )

    const handleStartCaptureClick = useCallback(() => {
        const stream = webcamRef.current?.stream

        logRecorder('start capture clicked', {
            hasWebcamRef: !!webcamRef.current,
            hasStream: !!stream,
            streamActive: stream?.active,
            existingRecorderState: mediaRecorderRef.current?.state
        })

        if (!stream) {
            logRecorder('start capture aborted; no webcam stream')
            return
        }

        logStreamTracks('webcam stream before recorder start', stream)

        const supportedVideoMimeType = getSupportedMimeTypes('video')
        const normalizedSupportedMimeType = normalizeMimeType(supportedVideoMimeType)

        logRecorder('selected recorder mime type', {
            supportedVideoMimeType,
            normalizedSupportedMimeType,
            isSupported: supportedVideoMimeType
                ? MediaRecorder.isTypeSupported(supportedVideoMimeType)
                : false
        })

        if (!supportedVideoMimeType) {
            setMediaRecordingSupported(false)
            return
        }

        recordedChunksRef.current = []
        recordedSizeRef.current = 0
        recordingStartedAtRef.current = Date.now()
        isClosingRef.current = false

        logRecorder('recording state reset', {
            recordingStartedAt: recordingStartedAtRef.current
        })

        start()

        try {
            mediaRecorderRef.current = new MediaRecorder(stream, {
                mimeType: supportedVideoMimeType
            }) as MediaRecorder
        } catch (error: any) {
            logRecorder('MediaRecorder constructor failed', {
                message: error?.message,
                name: error?.name,
                supportedVideoMimeType
            })

            recordingStartedAtRef.current = null
            setMediaRecordingSupported(false)
            return
        }

        const recorder = mediaRecorderRef.current

        logRecorder('MediaRecorder created', {
            state: recorder.state,
            mimeType: recorder.mimeType,
            audioBitsPerSecond: recorder.audioBitsPerSecond,
            videoBitsPerSecond: recorder.videoBitsPerSecond
        })

        recorder.addEventListener('dataavailable', handleDataAvailable)

        recorder.addEventListener('start', () => {
            logRecorder('recorder start event', {
                state: recorder.state,
                mimeType: recorder.mimeType
            })
        })

        recorder.addEventListener('stop', async () => {
            logRecorder('recorder stop event', {
                state: recorder.state,
                chunks: recordedChunksRef.current.length,
                totalSize: recordedSizeRef.current,
                totalSizeFormatted: formatBytes(recordedSizeRef.current)
            })

            await finalizeRecording(supportedVideoMimeType)
        })

        recorder.addEventListener('error', event => {
            logRecorder('recorder error event', {
                event,
                state: recorder.state,
                mimeType: recorder.mimeType
            })
        })

        try {
            recorder.start(RECORDING_TIMESLICE_MS)

            logRecorder('recorder.start called', {
                timeslice: RECORDING_TIMESLICE_MS,
                state: recorder.state
            })
        } catch (error: any) {
            logRecorder('recorder.start failed', {
                message: error?.message,
                name: error?.name
            })

            recordingStartedAtRef.current = null
            setMediaRecordingSupported(false)
            return
        }

        recordingTimeoutRef.current = window.setTimeout(() => {
            logRecorder('max duration reached; closing recorder', {
                maxDurationMs: MAX_RECORDING_DURATION_MS
            })

            closeRecorder()
        }, MAX_RECORDING_DURATION_MS)

        logRecorder('recording timeout scheduled', {
            maxDurationMs: MAX_RECORDING_DURATION_MS,
            maxSizeBytes: MAX_RECORDING_SIZE_BYTES,
            maxSizeFormatted: formatBytes(MAX_RECORDING_SIZE_BYTES)
        })
    }, [
        closeRecorder,
        finalizeRecording,
        handleDataAvailable,
        logStreamTracks,
        start
    ])

    const handleStopCaptureClick = useCallback(() => {
        logRecorder('stop capture clicked')
        closeRecorder()
    }, [closeRecorder])

    const handleCloseClick = useCallback(() => {
        logRecorder('modal close clicked', {
            type,
            status,
            isRunning,
            recorderState: mediaRecorderRef.current?.state
        })

        if (type === 'audio' && status === 'recording') {
            handleAudioStop()
        }

        closeRecorder()
    }, [closeRecorder, handleAudioStop, isRunning, status, type])

    if (!mediaRecordingSupported) {
        return (
            <div className='mediaRecorder'>
                <div className='mediaRecorder__header'>
                    <div></div>
                    <h3 className='mediaRecorder__header__title'>
                        {t('record')} {type}
                    </h3>
                    <div
                        className='mediaRecorder__header__close'
                        onClick={handleCloseClick}
                    >
                        <IconModalClose color='#fff' />
                    </div>
                </div>
                <div className='mediaRecorder__card'>
                    <div className='mediaRecorder__error'>
                        {t('mediaRecordingNotSupportedOnIos')}
                    </div>
                </div>
            </div>
        )
    }

    if (type === 'audio') {
        return (
            <div className='mediaRecorder mediaRecorder__audio'>
                <div className='mediaRecorder__header'>
                    <div></div>
                    <h3 className='mediaRecorder__header__title'>
                        {t('record')} {type}
                    </h3>
                    <div
                        className='mediaRecorder__header__close'
                        onClick={handleCloseClick}
                    >
                        <IconModalClose color='#fff' />
                    </div>
                </div>

                <div className='mediaRecorder__controls'>
                    <div className='mediaRecorder__controls__duration'>
                        {minutes}:{seconds}
                    </div>
                    <div className='mediaRecorder__controls__actions'>
                        <div
                            className={`mediaRecorder__controls__actions__wrapper ${
                                isRunning ? 'recording' : ''
                            }`}
                            onClick={() => {
                                logRecorder('audio action clicked', {
                                    status,
                                    isRunning
                                })

                                if (status === 'idle' || status === 'stopped') {
                                    isClosingRef.current = false
                                    start()
                                    handleAudioStart()
                                }

                                if (status === 'recording') {
                                    reset()
                                    pause()
                                    handleAudioStop()
                                    closeRecorder()
                                }
                            }}
                        >
                            {status === 'recording' ? (
                                <div className='mediaRecorder__controls__actions--stop'></div>
                            ) : (
                                <div className='mediaRecorder__controls__actions--record'></div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        )
    } else if (type === 'video') {
        return (
            <div className='mediaRecorder mediaRecorder__video' ref={ref}>
                <div className='mediaRecorder__header'>
                    <div></div>
                    <h3 className='mediaRecorder__header__title'>
                        {t('record')} {type}
                    </h3>

                    <div
                        className='mediaRecorder__header__close'
                        onClick={handleCloseClick}
                    >
                        <IconModalClose color='#fff' />
                    </div>
                </div>
                <>
                    <Webcam
                        audio
                        audioConstraints={{
                            echoCancellation: true
                        }}
                        forceScreenshotSourceSize={isMobile ? true : undefined}
                        mirrored={true}
                        muted
                        ref={webcamRef}
                        screenshotQuality={1}
                        videoConstraints={videoConstraints}
                        onUserMedia={stream => {
                            logRecorder('Webcam onUserMedia fired')
                            logStreamTracks('Webcam stream tracks', stream)
                        }}
                        onUserMediaError={(error: any) => {
                            logRecorder('Webcam onUserMediaError fired', {
                                message: error?.message,
                                name: error?.name,
                                constraint: error?.constraint
                            })

                            setMediaRecordingSupported(false)
                        }}
                        style={
                            width && width > 1050
                                ? {
                                      width: '100%'
                                  }
                                : {
                                      height: '100%'
                                  }
                        }
                    />
                    <div className='mediaRecorder__controls'>
                        <div className='mediaRecorder__controls__duration'>
                            {minutes}:{seconds}
                        </div>
                        <div
                            className={`mediaRecorder__controls__actions__wrapper ${
                                isRunning ? 'recording' : ''
                            }`}
                            onClick={() => {
                                if (!isRunning) {
                                    handleStartCaptureClick()
                                } else {
                                    handleStopCaptureClick()
                                }
                            }}
                        >
                            {isRunning ? (
                                <div className='mediaRecorder__controls__actions--stop'></div>
                            ) : (
                                <div className='mediaRecorder__controls__actions--record'></div>
                            )}
                        </div>
                    </div>
                </>
            </div>
        )
    } else return null
}

export default PostMediaRecorder